/**
 * Shared envelope returned by every Afya24 backend service. Always HTTP
 * 200; the `code` field tells us success/failure ("00" or "<X>-00").
 */
export interface ApiEnvelope<T> {
  code: string;
  data: T | null;
  errors: Array<{ code: string; field?: string; message: string }>;
  /** Optional metadata some gateway responses include. */
  timestamp?: string;
  requestId?: string;
}

/**
 * Paged-list envelope sitting inside `ApiEnvelope.data` for every
 * search/list endpoint that supports pagination. Mirrors the backend
 * record {@code com.afya24.common.response.PageResponse}.
 */
export interface PageResponse<T> {
  items: T[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

/** Query params accepted by every paged-list endpoint. */
export interface PageQuery {
  q?: string;
  page?: number;
  size?: number;
}

/** Success when the envelope code ends in "-00" (or is exactly "00"). */
export function isSuccess(envelope: { code?: string }): boolean {
  if (!envelope.code) return false;
  const suffix = envelope.code.split("-").pop() ?? "";
  return suffix === "00";
}

export type UserRole = "CUSTOMER" | "DOCTOR" | "ADMIN";
export type UserStatus = "ACTIVE" | "INACTIVE" | "SUSPENDED";

/**
 * Canonical Afya24 user record. Doctors are users with role DOCTOR plus
 * the optional clinical fields (specialization/licenseNumber/…), so the
 * doctor pages reuse this same shape — see {@link Doctor}.
 */
export interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  role: UserRole;
  status: UserStatus;
  photoUrl: string | null;
  /** Clinical fields — only populated for DOCTOR accounts. */
  doctorVerified?: boolean;
  specialization?: string | null;
  licenseNumber?: string | null;
  consultationFee?: number | null;
  qualifications?: string | null;
  yearsOfExperience?: number | null;
  certificateUrl?: string | null;
  doctorRejectionReason?: string | null;
  hospital?: string | null;
  rating?: number | null;
  /** Editable profile fields. */
  address?: string | null;
  gender?: string | null;
  bio?: string | null;
  createdAt: string;
}

/** A doctor is just a User with role DOCTOR and clinical fields filled. */
export type Doctor = User;

export interface Post {
  id: string;
  authorId: string;
  title: string;
  content: string | null;
  category: string | null;
  tags: string[] | null;
  mediaUrls: string[] | null;
  likesCount?: number;
  commentsCount?: number;
  viewsCount?: number;
  isFeatured?: boolean;
  isDraft: boolean;
  isPremium: boolean;
  takenDown?: boolean;
  takedownReason?: string | null;
  createdAt: string;
}

export type PostStatusFilter = "ALL" | "LIVE" | "DRAFT" | "TAKEN_DOWN";

export interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  imageUrl: string | null;
  categoryId: string | null;
  stock: number;
  rating: number | null;
  isActive: boolean;
  sellerId: string;
  createdAt: string;
}

// ── Finance / reporting shapes (wallet, payment, order services) ──

export interface DoctorRevenue {
  doctorId: string;
  totalEarnings: number;
  totalGross: number;
  commissionPaid: number;
  count: number;
}

export interface PlatformRevenue {
  totalCommission: number;
  count: number;
}

export type PaymentStatus =
  | "INITIATED" | "PENDING" | "PROCESSING" | "COMPLETED"
  | "FAILED" | "CANCELLED" | "REFUNDED" | "EXPIRED";

export interface Payment {
  id: string;
  userId: string;
  referenceId: string;
  externalTransactionId: string | null;
  provider: string;
  phoneNumber: string | null;
  amount: number;
  currency: string;
  status: PaymentStatus;
  paymentType: string | null;
  entityId: string | null;
  description: string | null;
  failureReason: string | null;
  initiatedAt: string | null;
  completedAt: string | null;
  createdAt?: string;
}

export interface PaymentSummary {
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  failedCount: number;
  completedAmount: number;
}

export interface Order {
  id: string;
  productId: string;
  customerId: string;
  sellerId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryPhone: string | null;
  status: string;
  isPaid: boolean | null;
  paymentReference: string | null;
  createdAt: string;
}

export type WithdrawalStatus = "PENDING" | "APPROVED" | "COMPLETED" | "REJECTED" | "CANCELLED";

export interface Withdrawal {
  id: string;
  userId: string;
  amount: number;
  availableBalance: number | null;
  status: WithdrawalStatus;
  paymentMethod: string | null;
  accountNumber: string | null;
  accountName: string | null;
  bankName: string | null;
  mobileProvider: string | null;
  notes: string | null;
  adminNotes: string | null;
  processedAt: string | null;
  transactionReference: string | null;
  createdAt: string;
}

export type AppointmentStatus = "PENDING" | "ACCEPTED" | "REJECTED" | "COMPLETED" | "CANCELLED";

export interface Appointment {
  id: string;
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  reason: string | null;
  status: AppointmentStatus;
  doctorResponse: string | null;
  isPaid: boolean | null;
  consultationFee: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Tokens returned by the OTP verify + refresh endpoints. */
export interface AuthTokens {
  userId: string;
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
  role: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  photoUrl?: string;
}

// ---- Catalog reference data (admin-managed) -------------------------------

export interface Specialization {
  id: string;
  name: string;
  nameSwahili: string;
  description: string | null;
  descriptionSwahili: string | null;
  category: string | null;
  iconUrl: string | null;
}

export interface ProductCategory {
  id: string;
  name: string;
  nameSwahili: string;
  description: string | null;
  iconName: string | null;
  isActive: boolean | null;
  displayOrder: number | null;
}

export interface Medicine {
  id: string;
  name: string;
  genericName: string;
  brandNames: string | null;
  category: string | null;
  drugClass: string | null;
  description: string | null;
  uses: string | null;
  sideEffects: string | null;
  dosageForm: string | null;
  strength: string | null;
  contraindications: string | null;
  interactions: string | null;
  pregnancyCategory: string | null;
  requiresPrescription: boolean | null;
  atcCode: string | null;
  isActive: boolean | null;
}

export interface LabTest {
  id: string;
  name: string;
  nameSwahili: string;
  testCode: string | null;
  category: string | null;
  description: string | null;
  descriptionSwahili: string | null;
  sampleType: string | null;
  fastingRequired: boolean | null;
  preparationInstructions: string | null;
  turnaroundTime: string | null;
  normalRange: string | null;
  unitOfMeasure: string | null;
  averagePrice: number | null;
  isActive: boolean | null;
}

// ---- Platform: settings, push, plans, audit, permissions ------------------

export interface AppSetting {
  key: string;
  value: string;
  isPublic: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface PushResult {
  audience: "ALL" | "USER" | "USERS";
  targetUsers: number | null;
  requested: number;
  sent: number;
  failed: number;
}

export interface PushLog {
  id: string;
  sentBy: string | null;
  audience: string;
  targetUsers: number | null;
  requested: number;
  sent: number;
  failed: number;
  title: string;
  body: string;
  deepLink: string | null;
  type: string | null;
  createdAt: string;
}

export interface UserStats {
  total: number;
  byRole: Record<string, number>;
  byStatus: Record<string, number>;
  verifiedDoctors: number;
  pendingDoctors: number;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  price: number;
  currency: string | null;
  durationDays: number;
  durationLabel: string | null;
  features: string | null;
  consultationsLimit: number | null;
  familyMembersLimit: number | null;
  prioritySupport: boolean | null;
  healthTracking: string | null;
  exclusiveContent: boolean | null;
  isActive: boolean | null;
  sortOrder: number | null;
}

export interface AuditEntry {
  id: string;
  requestId: string;
  serviceName: string;
  method: string;
  path: string;
  clientIdentity: string | null;
  statusCode: string | null;
  durationMs: number | null;
  ip: string | null;
  role: string | null;
  createdAt: string;
}

export interface AuditDetail extends AuditEntry {
  requestHeaders: Record<string, unknown> | null;
  requestPayload: unknown;
  responsePayload: unknown;
}

export interface PermissionCatalog {
  roles: { name: string; label: string; description: string }[];
  areas: { area: string; actions: { key: string; label: string; roles: string[] }[] }[];
}
